import React from 'react'
import { Flex, Text, Skeleton, SkeletonLine, SkeletonCircle } from '@chilipiper/design-system'
import { Avatar, Badge, IconButton } from '@chilipiper/design-system/src/new'
import { IconChevronLeft, IconChevronRight } from '@chilipiper/icons/figma-generated/src/icon'
import { useTranslations } from '../../../../context/TranslationContext'

type Props = {
  assignee?: { id: string; image?: string; name: string }
  displayName?: string
  state: 'scheduling' | 'rescheduling' | 'scheduled'
  durationInMinutes?: number
  onNextClick?: () => void
  onPreviousClick?: () => void
}

export const ScheduleHeader = ({
  displayName,
  assignee,
  state,
  durationInMinutes,
  onNextClick,
  onPreviousClick,
}: Props) => {
  const [pickTimeWithText, pickNewTimeWith, bookedWith] = useTranslations([
    'pickTimeWithText',
    'pickNewTimeWith',
    'bookedWith',
  ])
  return (
    <Flex
      px={5}
      pt={4}
      pb={5}
      flexDirection={'column'}
      alignItems={state === 'scheduled' ? 'center' : undefined}
      gap={4}
    >
      <Flex alignItems='flex-start' gap={2} justifyContent='space-between'>
        <Assignee assignee={assignee} />
        {durationInMinutes && state !== 'scheduled' ? (
          <Badge variant='neutral' label={`${durationInMinutes} min meeting`} />
        ) : undefined}
      </Flex>

      <Flex alignItems='flex-end' gap={2} justifyContent='space-between'>
        <Flex flexDirection='column' gap={1}>
          <Text
            color='fg/moderate'
            textStyle='detail-m'
            textAlign={state === 'scheduled' ? 'center' : undefined}
          >
            {state === 'scheduling' && pickTimeWithText}
            {state === 'rescheduling' && pickNewTimeWith}
            {state === 'scheduled' && bookedWith}
          </Text>
          {displayName !== undefined ? (
            <Text
              color='fg/major'
              textStyle={state === 'scheduled' ? 'page-section-heading' : 'heading-m'}
              textAlign={state === 'scheduled' ? 'center' : undefined}
            >
              {displayName}
            </Text>
          ) : (
            <Skeleton height={14} width={100}>
              <SkeletonLine y={6} width={100} />
            </Skeleton>
          )}
        </Flex>
        {state !== 'scheduled' && (
          <Flex gap={1}>
            {onPreviousClick && (
              <IconButton
                icon={IconChevronLeft}
                size={7}
                label='Previous Week'
                hasTooltip={false}
                variant='minor'
                onClick={onPreviousClick}
              />
            )}
            {onNextClick && (
              <IconButton
                icon={IconChevronRight}
                size={7}
                label='Next Week'
                hasTooltip={false}
                variant='minor'
                onClick={onNextClick}
              />
            )}
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}

const Assignee = ({
  assignee,
}: {
  assignee?: {
    id: string
    image?: string
    name: string
  }
}) => {
  if (!assignee) {
    return (
      <Skeleton width={56} height={56}>
        <SkeletonCircle cx={28} cy={28} r={28} />
      </Skeleton>
    )
  }

  return <Avatar id={assignee.id} name={assignee.name} size={10} image={assignee.image} />
}
